import { useBlocNext } from "@blac/react";
import styled from "@emotion/styled";
import clsx from "clsx";
import type { FC } from "react";
import React, { useEffect } from "react";
import ReactDOM from "react-dom";
import { IconCheckmarkButton } from "src/constants/icons";
import StatusBarBloc from "src/hybrid/components/StatusBarBloc";
import { type Category, DefaultCategory } from "src/lib/getEducationalContent";
import { useBloc } from "src/state/state";
import TaskManagementBloc from "src/state/TaskManagementBloc/TaskManagementBloc";
import Translate from "src/ui/components/Translate/Translate";
import ArticleCard from "src/ui/components/UserEducationalFeed/ArticleCard";
import type { EducationalContent } from "src/ui/components/UserEducationalFeed/UserEducationalFeedBloc";
import UserEducationalFeedBloc from "src/ui/components/UserEducationalFeed/UserEducationalFeedBloc";
import AsyncContent from "../AsyncContent/AsyncContent";

const ArticleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  gap: 1rem;
  margin-top: var(--space-sm);
`;

const ButtonRow = styled.div`
  display: flex;
  overflow-x: auto;
  gap: var(--space-xs);
  margin: -1em calc(var(--space-md) * -1) -2.5em -4.5em;
  padding: 1em var(--space-md) 2.5em 5em;

  //hide scrollbar all browsers
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }

  &[data-status-bar="true"] {
    margin: 0;
    padding: var(--space-sm) 0;
    padding-right: var(--space-md);
  }

  &[data-status-bar="true"]
    > nine-button.filter-button.is-selected:first-child {
    --large-shadow: 6px 8px 14px rgba(33, 33, 33, 0.18);
  }

  &[data-status-bar="false"] {
    margin-right: calc(var(--space-lg) * -3);
    padding-right: calc(var(--space-lg) * 3);
    margin-bottom: -2em;
  }

  @media screen and (min-width: 730px) {
    flex-wrap: wrap;
    overflow: initial;
  }

  @media (orientation: landscape) {
    [data-render-portal="true"] & {
      flex-wrap: initial;
      overflow-x: scroll;
      justify-content: safe center;
    }
  }
`;

const FeedErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-sm);
  padding: var(--space-md) 0;
  text-align: center;
`;

interface Props {
  categories: Category[];
  selectedCategory: Category;
  handleClickOnCategory: (category: Category, e?: Event) => void;
  isStatusBar: boolean;
}

const CategoryFilter: FC<Props> = (props: Props) => {
  return (
    <ButtonRow data-status-bar={props.isStatusBar}>
      {props.categories.map((category: Category) => (
        <nine-button
          class={clsx("filter-button", {
            "is-selected": props.selectedCategory === category
          })}
          size="sm"
          shadow="hover"
          key={category + props.isStatusBar.toString()}
          color="black"
          variant={props.selectedCategory === category ? "fill" : "outline"}
          arrow=""
          onClick={(e?: Event) => props.handleClickOnCategory(category, e)}
        >
          {props.selectedCategory === category && <IconCheckmarkButton />}
          <div style={{ marginLeft: "2px" }}>
            <Translate msg="category" variables={{ context: category }} />
          </div>
        </nine-button>
      ))}
    </ButtonRow>
  );
};

const UserEducationalFeed: FC = () => {
  const [selectedCategory, setSelectedCategory] = React.useState<Category>(
    DefaultCategory.all
  );
  const [{ status, articles, categories }, { loadEducationalContent }] =
    useBloc(UserEducationalFeedBloc, {
      create: () => new UserEducationalFeedBloc()
    });
  const [, { setRenderPortal }] = useBloc(StatusBarBloc);
  const [{ tasks: taskList }] = useBlocNext(TaskManagementBloc);

  const handleClickOnCategory = (category: Category, e?: Event) => {
    e?.preventDefault();
    if (category === selectedCategory) {
      setSelectedCategory(DefaultCategory.all);
    } else {
      setSelectedCategory(category);
    }
  };

  const filteredArticles = [
    ...new Set(
      selectedCategory === DefaultCategory.all
        ? articles
        : articles.filter((article: EducationalContent) =>
            article.categories.some(
              (category) => category.slug === selectedCategory
            )
          )
    )
  ];

  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 730);

  const taskSignature = taskList
    .map((t) => `${t.program}:${t.group}:${t.slug}:${t.status}`)
    .join("|");

  useEffect(() => {
    void loadEducationalContent(taskList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskSignature]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 730);
    };

    window.addEventListener("resize", handleResize, { passive: true });

    return () => {
      setRenderPortal(false);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const targetRef = React.useRef<HTMLDivElement>(null);
  const [hasScrolledPastFilter, setHasScrolledPastFilter] =
    React.useState(false);

  useEffect(() => {
    if (status !== "loaded") {
      setHasScrolledPastFilter(false);
      return undefined;
    }

    const updateFilterPosition = () => {
      const top = targetRef.current?.getBoundingClientRect().top;
      setHasScrolledPastFilter((top ?? Number.POSITIVE_INFINITY) < 0);
    };

    const frame = window.requestAnimationFrame(updateFilterPosition);

    const ionContent = document.querySelector("ion-content");
    const scrollable = ionContent?.shadowRoot?.querySelector(".inner-scroll");

    scrollable?.addEventListener("scroll", updateFilterPosition, {
      passive: true
    });
    window.addEventListener("scroll", updateFilterPosition, { passive: true });
    window.addEventListener("resize", updateFilterPosition, { passive: true });

    return () => {
      window.cancelAnimationFrame(frame);
      scrollable?.removeEventListener("scroll", updateFilterPosition);
      window.removeEventListener("scroll", updateFilterPosition);
      window.removeEventListener("resize", updateFilterPosition);
    };
  }, [status]);

  const renderCategoryFilterAsTopBar = isMobile && hasScrolledPastFilter;

  useEffect(() => {
    if (
      status === "loaded" &&
      categories.length > 0 &&
      renderCategoryFilterAsTopBar
    ) {
      setRenderPortal(true);
    } else {
      setRenderPortal(false);
    }
  }, [renderCategoryFilterAsTopBar, status, categories.length]);

  const feedSkeletons = (
    <ArticleGrid>
      {new Array(4).fill("").map((_, index) => (
        <AsyncContent key={index} height="6rem" />
      ))}
    </ArticleGrid>
  );

  const feedError = (
    <FeedErrorContainer>
      <p>
        <Translate msg="error_generic" />
      </p>
      <nine-button
        onClick={() => void loadEducationalContent(taskList)}
        size="sm"
        variant="fill"
        color="black"
      >
        <Translate msg="retry" />
      </nine-button>
    </FeedErrorContainer>
  );

  const feedContent = (
    <>
      <div ref={targetRef}>
        <CategoryFilter
          data-render-portal={renderCategoryFilterAsTopBar}
          categories={categories}
          selectedCategory={selectedCategory}
          handleClickOnCategory={handleClickOnCategory}
          isStatusBar={false}
        />
      </div>
      <div>
        {renderCategoryFilterAsTopBar &&
          ReactDOM.createPortal(
            <CategoryFilter
              isStatusBar={true}
              categories={categories}
              selectedCategory={selectedCategory}
              handleClickOnCategory={handleClickOnCategory}
            />,
            //eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            document.getElementById("statusBarPortalTarget")!
          )}
      </div>
      <ArticleGrid>
        {filteredArticles.map((article, index) => (
          <ArticleCard key={index} article={article} />
        ))}
      </ArticleGrid>
    </>
  );

  const body =
    status === "loading"
      ? feedSkeletons
      : status === "error"
        ? feedError
        : feedContent;

  return <div>{body}</div>;
};

export default UserEducationalFeed;
